import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { isEmpty, isNil, isString } from 'lodash'
import { IconActivity, IconChevronRight, IconTool } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { setHardSelectedEvent } from '@/reducers/media/media.actions'
import { formatDuration } from '@/utils/helpers'

import {
  HARD_SELECTED_EVENT_ID,
  removeCallExplorerParams,
  SECTION_NAMES,
  TOOL_CALL_SECTION,
  updateCallExplorerParams,
} from './helpers'

// `arguments` and `metadata` are opaque payloads whose shape differs per tool and
// per event type, and new shapes ship without a front-end change — so render them
// generically rather than switching on the tool or event name to pull named fields.
const formatPayloadValue = (value) => (isString(value) ? value : JSON.stringify(value))

const PayloadTable = ({ label, payload, testId }) => {
  if (isEmpty(payload)) {
    return null
  }

  return (
    <section className="agent-activity-section" data-testid={testId}>
      <h4 className="agent-activity-section-label">{label}</h4>
      <dl className="agent-activity-kv">
        {Object.entries(payload).map(([key, value]) => (
          <React.Fragment key={key}>
            <dt>{key}</dt>
            <dd>{formatPayloadValue(value)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  )
}

// An event's `level` is nullable, and a null level alongside a null message is the
// signal that upstream received an envelope it could not parse and preserved the
// whole thing in `metadata`. Substituting a default here would present that as a
// routine informational event, so null gets its own label rather than a default.
// A level we don't recognise is shown verbatim for the same reason — the set is
// open, and an allowlist would make a new level render as though it were unknown.
const getLevelLabel = (level) => {
  if (isNil(level)) {
    return 'Unknown'
  }
  if (level === 'error') {
    return 'Error'
  }
  if (level === 'info') {
    return 'Info'
  }
  return level
}

export const AgentActivityBlock = ({ event }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const [expanded, setExpanded] = useState(false)

  const {
    id,
    timestamp,
    section,
    name,
    arguments: toolArguments,
    result,
    level,
    message,
    metadata,
  } = event

  const {
    mediaPlayer: { hardSelectedEvent },
  } = useSelector((state) => state.media)

  const isToolCall = section === TOOL_CALL_SECTION
  const isHardSelectedEvent = hardSelectedEvent?.id === id

  // A tool call that returned always carries a non-empty string; null means it was
  // invoked and never returned — the call ended mid-tool, or the tool errored. That
  // distinction is meaningful, so it is on the row rather than hidden behind expand.
  const hasResult = !isNil(result)
  const hasDetails = isToolCall ? !isEmpty(toolArguments) || hasResult : !isEmpty(metadata)

  const stateLabel = isToolCall ? (hasResult ? 'Returned' : 'No result') : getLevelLabel(level)
  // Error is the one state that keeps a colour cue, and only on the text — an error
  // event should not read as routine. Everything else matches the neutral category
  // pill the other event rows use.
  const isErrorState = !isToolCall && level === 'error'

  // Matches EventBlock: clicking the row seeks the player to this timestamp, which
  // scrolls the transcript to the nearest block. Expanding is a separate affordance
  // so opening details doesn't move playback.
  const onRowClick = () => {
    if (isHardSelectedEvent) {
      removeCallExplorerParams(location, history, ['eventId'])
    } else {
      updateCallExplorerParams(location, history, { eventId: id, timestamp })
    }
    dispatch(setHardSelectedEvent({ id, timestamp, displayText: name, section }))
  }

  const onToggleClick = (clickEvent) => {
    clickEvent.stopPropagation()
    setExpanded(!expanded)
  }

  // The expanded payload sits inside the row's click target, so without this a
  // click-drag to select text in a result or argument value fires the row click on
  // mouseup — seeking playback, pushing history, and de-selecting an already
  // hard-selected row. Reading the details should not move the player.
  const onDetailsClick = (clickEvent) => clickEvent.stopPropagation()

  return (
    <div className="events-block">
      <div
        id={isHardSelectedEvent ? HARD_SELECTED_EVENT_ID : ''}
        className={classNames('events-main-container', { 'hard-selected': isHardSelectedEvent })}
        onClick={onRowClick}
        data-testid={`agent-activity-row-${id}`}
      >
        <div className="event-start-time">{formatDuration(timestamp)}</div>
        <div className="event-detail-container">
          <div className="event-section-category agent-activity-category">
            {/* Tool calls and agent events share one neutral fill — every hue in the
                section palette is already spoken for — so the glyph is what makes the
                two kinds scannable without reading each label. IconTool matches what
                already denotes a tool in the Voice Agents Tools tab. */}
            <span
              className={classNames(
                'section-category-label agent-activity-kind',
                `event-section-${section}`
              )}
            >
              {isToolCall ? (
                <IconTool className="icon-svg" />
              ) : (
                <IconActivity className="icon-svg" />
              )}
              {SECTION_NAMES[section]}
            </span>
            <span
              className={classNames('section-category-label event-category', {
                'agent-activity-state--error': isErrorState,
              })}
              data-testid={isToolCall ? `tool-call-status-${id}` : `agent-event-level-${id}`}
            >
              {stateLabel}
            </span>
          </div>
          <span className="event-item agent-activity-name">{name}</span>
          {Boolean(message) && <span className="agent-activity-message">{message}</span>}
          {expanded && (
            <div
              className="agent-activity-details"
              onClick={onDetailsClick}
              data-testid={`agent-activity-details-${id}`}
            >
              {isToolCall ? (
                <>
                  <PayloadTable
                    label="Arguments"
                    payload={toolArguments}
                    testId={`tool-call-arguments-${id}`}
                  />
                  {hasResult && (
                    <section className="agent-activity-section">
                      <h4 className="agent-activity-section-label">Result</h4>
                      {/* Results are written for the agent, not for people — they run
                          to several lines and can carry text-to-speech markup, so they
                          get their own scroll box rather than reflowing the panel. */}
                      <pre className="agent-activity-result" data-testid={`tool-call-result-${id}`}>
                        {result}
                      </pre>
                    </section>
                  )}
                </>
              ) : (
                <PayloadTable
                  label="Metadata"
                  payload={metadata}
                  testId={`agent-event-metadata-${id}`}
                />
              )}
            </div>
          )}
        </div>
        {hasDetails && (
          <Button
            xs
            className="agent-activity-toggle"
            icon={<IconChevronRight className="icon-svg" />}
            onClick={onToggleClick}
            aria-expanded={expanded}
            data-testid={`agent-activity-toggle-${id}`}
          >
            {expanded ? 'Hide' : 'Details'}
          </Button>
        )}
      </div>
    </div>
  )
}
